/**
 * The thread of a submission, in the panel and on the triage surface.
 *
 * This is the half of the design that makes the panel a conversation rather
 * than a form that closes: a panel that looks like a chat promises an answer
 * in the same place, so the answer comes back here. That promise is also why a
 * submission is a thread of messages instead of one text plus a note from the
 * triager, and why that is one table rather than one column (FR-22).
 *
 * Append only. There is no route that changes a message and none that removes
 * one, so there is no edit control here either.
 *
 * The component owns the split between the part that scrolls and the composer
 * that does not. A thread is the state where that matters most: the panel is
 * 620 tall on every screen and a fourth message is not, and a composer that
 * scrolls away with the conversation is a composer somebody cannot find.
 */
import { Button, TextArea } from "@depicomnlbv/platform-ui";
import { useState, type ReactNode } from "react";

import { copy } from "./copy";
import type { ThreadMessage } from "./types";
import { Icon } from "./ui";

export interface ThreadProps {
  messages: ThreadMessage[];
  onSend: (body: string) => void;
  /** True while a message is on its way, so the button says so rather than spinning. */
  isSending?: boolean;
  /** Rendered in the scrolling part above the messages. */
  above?: ReactNode;
  /** Rendered in the scrolling part below them, pushed to the bottom. */
  below?: ReactNode;
}

/** A time a person reads, in the reader's own locale. */
function when(at: string): string {
  return new Date(at).toLocaleString(undefined, {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Thread({ messages, onSend, isSending = false, above, below }: ThreadProps) {
  const [draft, setDraft] = useState("");
  const answered = messages.some((message) => message.authorSide === "triager");

  function send() {
    const body = draft.trim();
    // An empty message is a row nobody can read, and the API refuses it. A
    // button that provokes an error is a bad screen, so this one does nothing.
    if (!body) return;
    onSend(body);
    setDraft("");
  }

  return (
    <>
      <div className="pf-panel__body">
        {above}
        <ul className="pf-thread">
          {messages.map((message) => (
            <li key={message.id} className={`pf-message pf-message--${message.authorSide}`}>
              <p className="pf-message__body">{message.body}</p>
              <p className="pf-message__meta">
                {/* Which side wrote this is carried by a name as well as by the
                    alignment and the ground, because a reader who cannot see
                    either still has to tell an answer from his own words. */}
                {message.authorSide === "reporter" ? copy.thread.fromReporter : message.authorName}
                {" · "}
                {when(message.at)}
              </p>
            </li>
          ))}
          {answered ? null : (
            // Not a blank space under a sent report, which reads as a failure.
            // What is true is that it arrived and nobody has replied yet.
            <li className="pf-awaiting">{copy.thread.nobodyAnswered}</li>
          )}
        </ul>
        {below}
      </div>

      <div className="pf-panel__foot">
        <div className="pf-composer">
          {/* The canvas drew "Add something" inside the field until
              9 September. It is a visible label above the composer now: an
              accessible name alone leaves the sighted reader with an unlabelled
              box, and platform-ui has no placeholder on purpose. */}
          <TextArea
            label={copy.thread.compose}
            value={draft}
            onChange={setDraft}
            rows={1}
          />
          <Button
            variant="primary"
            onPress={send}
            isPending={isSending}
            pendingLabel={copy.form.sending}
            aria-label={isSending ? copy.form.sending : copy.thread.sendMessage}
          >
            <Icon name="send" />
          </Button>
        </div>
      </div>
    </>
  );
}
